import React from "react"

import type { AnalyticsTopSalespersonRow } from "../redux/global-map.types"
import { ANALYTICS_ENTITY_COLOR } from "./analytics-constants"

interface TopSalespeopleCardProps {
  rows: AnalyticsTopSalespersonRow[]
  onSelectSalesperson: (userId: string) => void
}

const SEGMENT_ORDER = ["project", "lead", "subcontractor", "appointment"]

function initials(name: string | null): string {
  if (!name) return "?"
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?"
}

/**
 * Spec §8: top 5 by record count (already capped server-side), the
 * explicit `sales_manager` relationship (not record creator). "Unassigned"
 * is server-provided and not clickable — no single salesperson id to apply.
 */
export function TopSalespeopleCard({
  rows,
  onSelectSalesperson
}: TopSalespeopleCardProps) {
  const maxTotal = Math.max(...rows.map((row) => row.total), 1)

  return (
    <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
      {rows.map((row) => {
        const isClickable = row.userId !== null
        const rowContent = (
          <>
            <span
              aria-hidden
              className="flex size-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-600"
            >
              {initials(row.name)}
            </span>
            <span className="w-24 shrink-0 truncate text-xs text-slate-700">
              {row.name}
            </span>
            <div className="h-4 flex-1 overflow-hidden rounded bg-gray-100">
              <div
                className="flex h-full"
                style={{ width: `${(row.total / maxTotal) * 100}%` }}
              >
                {SEGMENT_ORDER.filter((key) => row.byEntityType[key]).map(
                  (key) => (
                    <div
                      key={key}
                      style={{
                        width: `${(row.byEntityType[key] / row.total) * 100}%`,
                        backgroundColor: ANALYTICS_ENTITY_COLOR[key]
                      }}
                    />
                  )
                )}
              </div>
            </div>
            <span className="w-8 shrink-0 text-right text-xs font-semibold text-gray-900">
              {row.total}
            </span>
          </>
        )

        return (
          <li
            key={row.userId ?? "unassigned"}
            className="flex items-center gap-2"
          >
            {isClickable ? (
              <button
                type="button"
                onClick={() => row.userId && onSelectSalesperson(row.userId)}
                className="flex w-full items-center gap-2 rounded text-left hover:bg-gray-50"
              >
                {rowContent}
              </button>
            ) : (
              <div className="flex w-full items-center gap-2">{rowContent}</div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
